import { AxiosError } from 'axios';
import z from 'zod';
import { COUNTRIES, ERROR_MESSAGES, LIMITED_COUNTRY_OPTIONS, STATES } from '~/constants';
import { MedicalBoard, ProviderAddress } from '~/types';
import {
  SESSION_REFRESH_SUBTEXT,
  SESSION_REFRESH_TEXT,
  isMiddlewareAuthRejection,
  requestSentBearerToken,
} from '~/utils/authErrors';
import { Address, PhoneNumber } from '../types';
import { AddressFormValues, CustomAlertMessagePayload, OptionWithIdType } from './types';
import { AddressType, OptionStringType, PhoneFormValues, PhoneType } from './vault/profile/types';

import { OnShowAlert } from './alerts';
import { ShowConfirmationEvent } from './user/events';

export const isValidDate = (dateString: string) => {
  const date = new Date(dateString);
  return !isNaN(date.getTime()) && dateString !== '';
};

export const convertWarningsKeys = (
  warnings?: Record<string, string> | null
): Record<string, string> => {
  if (!warnings || typeof warnings !== 'object') {
    return {};
  }

  return Object.entries(warnings).reduce<Record<string, string>>((result, [key, value]) => {
    const camelKey = key.replace(/_([a-z])/g, (_match: string, letter: string): string =>
      letter.toUpperCase()
    );
    result[camelKey] = value;
    return result;
  }, {});
};

/**
 * Finds the option whose value matches `code`. Returns `fallbackOption` when no match
 * is found. A fallback is required to prevent silent substitution with the first option
 * (which historically rendered Afghanistan for unknown countries, Alabama for unknown
 * states, etc.). Callers must pick a fallback that is correct for their context —
 * typically EMPTY_OPTION for forms, or a relation-derived value for merge flows.
 */
export function transformCodeToSelectOption(
  code: string,
  optionsArray: OptionStringType[],
  fallbackOption: OptionStringType
): OptionStringType {
  const option = optionsArray.find((option) => option.value === code);
  return option ?? fallbackOption;
}

export function transformTypeToOption(type: string): OptionStringType {
  return {
    value: type,
    label: type,
  };
}

const isStrictISODate = (s: string): boolean => {
  const match = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(s);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (month < 1 || month > 12) return false;
  if (match[3] !== undefined) {
    const day = Number(match[3]);
    if (day < 1) return false;
    const ts = Date.UTC(year, month - 1, day);
    const d = new Date(ts);
    if (d.getUTCFullYear() !== year || d.getUTCMonth() + 1 !== month || d.getUTCDate() !== day) {
      return false;
    }
  }
  return true;
};

export const formatCredentialDate = (dateAsString: string): string => {
  if (!dateAsString) return '';
  if (!isStrictISODate(dateAsString)) return '';
  const d = new Date(dateAsString);
  if (isNaN(d.getTime())) return '';
  // [MCV-1811] A CV that gives only a year is stored as Jan 1, so Jan 1 shows just the year.
  // Every other date is "Mon yyyy": a 1st-of-month date used to read "07/99", which put two
  // formats in one cell ("07/99 – Jun 2000").
  if (d.getUTCMonth() === 0 && d.getUTCDate() === 1) {
    return String(d.getUTCFullYear());
  }
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
};

export const dateStringFormatter = (
  dateAsString: string,
  type: 'STRING' | 'NUMERIC' | 'SHORT_DATE' | 'LONG_DATE' = 'STRING'
) => {
  switch (type) {
    case 'STRING':
      return new Date(dateAsString).toLocaleDateString(undefined, {
        weekday: undefined,
        day: undefined,
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      });
    case 'NUMERIC':
      return new Date(dateAsString).toLocaleDateString(undefined, {
        weekday: undefined,
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      });
    case 'SHORT_DATE':
      return new Date(dateAsString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      });
    case 'LONG_DATE':
      return new Date(dateAsString).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      });
  }
};

export function formatFormAddressesForApi(addresses: AddressFormValues[]): Address[] {
  const formattedArray: Address[] = [];
  addresses.forEach((address) => {
    const formattedAddress = formatFormAddressForApi(address);
    formattedArray.push(formattedAddress);
  });
  return formattedArray;
}

export function formatFormAddressForApi(address: AddressFormValues): Address | ProviderAddress {
  return {
    ...address,
    addressType: address.addressType?.value as AddressType,
    addressLine2: address.addressLine2 || undefined,
    postalCode: `${address.postalCode}`,
    stateCode: address.stateCode.value,
    countryCode: address.countryCode.value,
  };
}

export const hasAddressContent = (address?: Partial<AddressFormValues>): boolean =>
  Boolean(address?.addressLine1?.trim() || address?.city?.trim() || address?.postalCode?.trim());

// Capitalizes the first LETTER of every space-separated word and lowercases the
// rest. Leading punctuation does not block it ("(downtown)" -> "(Downtown)"),
// but a word that opens with a digit keeps its case ("1st" stays "1st").
export function capitalizeEveryWord(text: string): string {
  if (!text) return '';
  return text
    .toLowerCase()
    .split(' ')
    .filter(Boolean)
    .map((word) =>
      word.replace(/^([^\p{L}\p{N}]*)(\p{L})/u, (_, lead, letter) => lead + letter.toUpperCase())
    )
    .join(' ');
}

// [MCV-1876] Removes trailing " (City, ST)" groups that match the facility's OWN
// address, repeatedly and case-insensitively, so names stored with the picker
// suffix (sometimes twice, in mixed case) render clean until they are backfilled.
// Only the facility's own location is ever stripped: "Dallas (Downtown)" is part
// of the name and stays. The name is never reduced to nothing.
export const stripLocationSuffix = (
  name: string,
  city?: string | null,
  stateCode?: string | null
): string => {
  const location = [city?.trim(), stateCode?.trim()].filter(Boolean).join(', ');
  if (!location) return name;
  const suffix = ` (${location})`.toLowerCase();
  let result = name.trimEnd();
  for (;;) {
    if (result.length <= suffix.length || !result.toLowerCase().endsWith(suffix)) break;
    const next = result.slice(0, result.length - suffix.length).trimEnd();
    if (!next) break;
    result = next;
  }
  return result;
};

export function formatFormPhoneNumbersForApi(numbers: PhoneFormValues[]): PhoneNumber[] {
  const formattedArray: PhoneNumber[] = [];
  numbers.forEach((numbObj) => {
    const formattedNumber: PhoneNumber = {
      ...numbObj,
      phoneType: numbObj.phoneType.value as PhoneType,
      countryCode: numbObj.countryCode.value,
    };
    formattedArray.push(formattedNumber);
  });
  return formattedArray;
}

/**
 * Pull a human-readable string out of the backend's error detail field.
 *
 * The Litestar API returns `{ error: string, detail: string | object | array, extra?: object }`.
 * `detail` is typically a string, but for validation errors it can be a list of
 * `{ loc, msg, type }` records (pydantic-style). This helper normalizes each
 * shape into a single string safe to show in a toast subtext.
 *
 * Exported for unit testing.
 */
export const extractBackendErrorDetail = (detail: unknown): string | undefined => {
  if (detail == null || detail === '') return undefined;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const lines = detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          const obj = item as { msg?: unknown; message?: unknown; loc?: unknown };
          const msg =
            typeof obj.msg === 'string'
              ? obj.msg
              : typeof obj.message === 'string'
                ? obj.message
                : undefined;
          if (msg) {
            const field = Array.isArray(obj.loc)
              ? obj.loc.filter((p) => typeof p === 'string' || typeof p === 'number').join('.')
              : '';
            return field ? `${field}: ${msg}` : msg;
          }
        }
        return undefined;
      })
      .filter((line): line is string => Boolean(line));
    return lines.length > 0 ? lines.join('\n') : undefined;
  }
  if (typeof detail === 'object') {
    const obj = detail as { msg?: unknown; message?: unknown; detail?: unknown };
    if (typeof obj.msg === 'string') return obj.msg;
    if (typeof obj.message === 'string') return obj.message;
    if (typeof obj.detail === 'string') return obj.detail;
    return undefined;
  }
  return undefined;
};

// [MCV-1838] `error` value of the API's generic handler
// (mcv_api/exceptions/handlers.py http_exception_handler).
const GENERIC_HTTP_EXCEPTION_HEADLINE = 'HTTP Exception';

const headlineForStatus = (status: number | undefined): string => {
  switch (status) {
    case 400:
      return "Couldn't complete that";
    case 403:
      return 'Not allowed';
    case 404:
      return 'Not found';
    case 409:
      return 'Already exists';
    case 422:
      return 'Check the details';
    default:
      return 'Something went wrong';
  }
};

export const sendAxiosErrorAlert = (event: { error?: unknown }) => {
  const { error } = event;

  // The API's auth middleware rejected the session token and the one silent
  // refresh-and-retry in apiAxiosV1 did not recover it. Show actionable copy
  // instead of the raw 'Not Authorized' / 'Requestor is not an authenticated
  // user'. Only for a request that carried a Bearer token: one sent without a
  // token was never retried, and refreshing the page would not help it. Other
  // 401s (guard and handler permission denials) keep the backend text.
  if (isMiddlewareAuthRejection(error) && requestSentBearerToken(error)) {
    return {
      type: 'ON_SHOW_ALERT',
      payload: {
        text: SESSION_REFRESH_TEXT,
        subText: SESSION_REFRESH_SUBTEXT,
        type: 'error' as const,
        timeout: 5000,
      },
    };
  }

  const isAxios = error != null && typeof error === 'object' && 'response' in error;
  const axiosError = isAxios ? (error as AxiosError) : undefined;
  const statusText = axiosError?.response?.statusText;
  const fallbackMessage =
    import.meta.env.VITE_DASH0_ENV === 'prod' || !(error instanceof Error)
      ? 'Please try again'
      : error.message;

  // Surface the backend's own error payload when available. Litestar's exception
  // handlers return `{ error, detail, extra? }`; we prefer that over Axios'
  // generic "Request failed with status code N" so the user sees what actually
  // went wrong (e.g. "email is already in use" instead of "Error occurred").
  const responseData = axiosError?.response?.data as
    | { error?: unknown; detail?: unknown; extra?: { code?: unknown } }
    | undefined;

  // [MCV-1838] The API's generic HTTPException handler answers every
  // HTTPException/ClientException with the class name as `error`
  // ("HTTP Exception"), which is not a headline. Treat it as absent and title
  // the toast by status; the backend `detail` still carries the message.
  const rawHeadline =
    typeof responseData?.error === 'string' && responseData.error.trim()
      ? responseData.error.trim()
      : undefined;
  const isGenericHeadline =
    rawHeadline?.toLowerCase() === GENERIC_HTTP_EXCEPTION_HEADLINE.toLowerCase();
  // [MCV-1926] An expired provider invitation is a 409, but not "Already exists".
  const isExpiredInvitation =
    axiosError?.response?.status === 409 && responseData?.extra?.code === 'INVITATION_EXPIRED';
  const backendHeadline =
    isGenericHeadline || (isExpiredInvitation && !rawHeadline)
      ? isExpiredInvitation
        ? 'Invitation expired'
        : headlineForStatus(axiosError?.response?.status)
      : rawHeadline;
  const backendDetail = extractBackendErrorDetail(responseData?.detail);

  const text = backendHeadline ?? (statusText ? `Error: ${statusText}` : 'An error occurred');
  const subText = backendDetail ?? fallbackMessage;

  return {
    type: 'ON_SHOW_ALERT',
    payload: {
      text,
      subText,
      type: 'error' as const,
      timeout: 5000,
    },
  };
};

export const sendErrorAlert = (payload: CustomAlertMessagePayload): OnShowAlert => {
  return {
    type: 'ON_SHOW_ALERT',
    payload: {
      text: payload.text || 'An error occurred',
      subText: payload.subText,
      type: 'error' as const,
      timeout: 5000,
    },
  };
};

export const sendSuccessAlert = (payload: CustomAlertMessagePayload): OnShowAlert => {
  return {
    type: 'ON_SHOW_ALERT',
    payload: {
      text: payload.text || 'Success',
      subText: payload.subText,
      type: 'success' as const,
      timeout: 5000,
    },
  };
};

export const sendDeleteConfirmation = (onConfirm: () => void): ShowConfirmationEvent => {
  return {
    type: 'ON_SHOW_CONFIRMATION',
    payload: {
      title: 'Delete Confirmation',
      message: 'Are you sure you want to delete? This action cannot be undone.',
      onConfirm,
    },
  };
};

export function transformAddressToFormValues(address: Address): AddressFormValues {
  return {
    addressType: undefined,
    addressLine1: address.addressLine1 || '',
    addressLine2: address.addressLine2 || '',
    city: address.city || '',
    postalCode: address.postalCode || '',
    // [MCV-1374] Preserve non-US province codes on load. If the stored
    // stateCode isn't a US state, pass it through as {value, label} rather
    // than collapsing to STATES[0] (Alabama).
    stateCode: address.stateCode
      ? (STATES.find((s) => s.value === address.stateCode) ?? {
          value: address.stateCode,
          label: address.stateCode,
        })
      : STATES[0],
    // [MCV-1374] Look up the country against the full COUNTRIES list so a
    // stored non-US countryCode is preserved on load. Falls back to US for
    // empty/unknown values to match the prior default.
    countryCode: address.countryCode
      ? transformCodeToSelectOption(address.countryCode, COUNTRIES, LIMITED_COUNTRY_OPTIONS[0])
      : LIMITED_COUNTRY_OPTIONS[0],
  };
}

// Inverse of transformAddressToFormValues: collapse the select-option address
// form values back into the flat Address shape the API and PeerReference expect.
// Shared by the address wizard page's submit handler and saveWizardDraft so the
// two stay in lockstep (MCV-1488).
export function transformFormValuesToAddress(values: AddressFormValues): Address {
  return {
    ...values,
    addressLine2: values.addressLine2 || undefined,
    stateCode: values.stateCode.value,
    countryCode: values.countryCode.value,
  };
}

export const medicalBoardResponseToOptions = (data: string): OptionWithIdType[] => {
  const boards: MedicalBoard[] = JSON.parse(data);
  return boards.map((board: MedicalBoard) => ({
    label: board.boardName,
    value: board.boardCode,
    id: board.id.toString(),
  }));
};

export const nonNegativeInt = z.preprocess(
  (arg) => (arg === null || arg === '' ? 0 : Number(arg)),
  z.coerce
    .number()
    .min(0, ERROR_MESSAGES.NON_NEGATIVE_INT)
    .int(ERROR_MESSAGES.WHOLE_NUMBER)
    .optional()
    .nullable()
);

export const positiveInt = z.preprocess(
  (arg) => (arg === null || arg === '' ? 0 : Number(arg)),
  z.coerce
    .number()
    .min(1, ERROR_MESSAGES.POSITIVE_INT)
    .int(ERROR_MESSAGES.WHOLE_NUMBER)
    .optional()
    .nullable()
);

export const positiveNumber = z.preprocess(
  (arg) => (arg === null || arg === '' ? 0 : Number(arg)),
  z.coerce.number().min(0, ERROR_MESSAGES.NON_NEGATIVE_INT).optional().nullable()
);
/**
 * Returns a realtive time string comparing two dates
 * ex - 1 hour ago, 1 day ago, etc.
 * @param d1
 * @param d2 defaults to today
 * @returns
 */

export const getRelativeTime = (d1: Date, d2 = new Date()) => {
  type Unit = keyof typeof units;
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  const elapsed = d1.getTime() - d2.getTime();
  const units = {
    year: 24 * 60 * 60 * 1000 * 365,
    month: (24 * 60 * 60 * 1000 * 365) / 12,
    day: 24 * 60 * 60 * 1000,
    hour: 60 * 60 * 1000,
    minute: 60 * 1000,
    second: 1000,
  } as const;

  for (const u of Object.keys(units) as Unit[]) {
    const ms = units[u];
    if (Math.abs(elapsed) > ms || u === 'second') {
      return rtf.format(Math.round(elapsed / ms), u as Intl.RelativeTimeFormatUnit);
    }
  }
};

export const getInitials = (name: string): string =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
